<script setup lang="ts">
import { ref } from 'vue'
import { reportSupport } from '@/api/errorReportingApi'
import TheIcon from '@/components/atoms/TheIcon/TheIcon.vue'

const showModal = ref(false)
const submitted = ref(false)
const loading = ref(false)
const submissionSuccess = ref(false)

const form = ref({
  name: '',
  email: '',
  phone: '',
  message: ''
})

const errors = ref<Record<string, string>>({})

function validateForm(): boolean {
  errors.value = {}
  
  if (!form.value.name.trim()) {
    errors.value.name = 'Navn er påkrævet'
  }
  
  if (!form.value.email.trim()) {
    errors.value.email = 'Email er påkrævet'
  } else if (!form.value.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    errors.value.email = 'Ugyldig email-adresse'
  }
  
  if (!form.value.message.trim()) {
    errors.value.message = 'Besked er påkrævet'
  }
  
  return Object.keys(errors.value).length === 0
}

async function submitForm() {
  if (!validateForm()) return
  
  loading.value = true
  submitted.value = true
  
  try {
    // Get device and browser information
    const userAgent = navigator.userAgent
    const browserInfo = getBrowserInfo(userAgent)
    const timestamp = new Date().toLocaleString('da-DK')
    const currentUrl = window.location.href
    
    const technicalDetails = `
- **Browser:** ${browserInfo.browser} ${browserInfo.version}
- **Operativsystem:** ${browserInfo.os}
- **Enhed:** ${getBrowserDeviceType()}
- **URL:** ${currentUrl}
- **Tid:** ${timestamp}
- **User Agent:** ${userAgent}
    `.trim()

    const userDetails = `
Supportanmodning fra: ${form.value.name} (${form.value.email})
Telefon: ${form.value.phone || 'Ikke angivet'}

Besked:
${form.value.message}
    `.trim()

    await reportSupport({
      errorMessage: 'User Support Request',
      errorDetails: technicalDetails,
      userAdditionalInfo: userDetails
    })
    
    // Show success message
    submissionSuccess.value = true
    
    // Auto-close modal after 5 seconds
    setTimeout(() => {
      resetForm()
      showModal.value = false
      submissionSuccess.value = false
    }, 5000)
  } catch (error) {
    console.error('Failed to submit support request:', error)
    submissionSuccess.value = false
    alert('Der opstod en fejl ved indsendelse af din henvendelse. Prøv igen senere.')
  } finally {
    loading.value = false
  }
}

function resetForm() {
  form.value = {
    name: '',
    email: '',
    phone: '',
    message: ''
  }
  errors.value = {}
  submitted.value = false
}

function closeModal() {
  showModal.value = false
  resetForm()
}

// Helper functions for browser and device detection
function getBrowserInfo(userAgent: string): { browser: string; version: string; os: string } {
  let browser = 'Unknown'
  let version = 'Unknown'
  let os = 'Unknown'
  
  // Detect OS
  if (userAgent.includes('Windows')) os = 'Windows'
  else if (userAgent.includes('Mac')) os = 'macOS'
  else if (userAgent.includes('Linux')) os = 'Linux'
  else if (userAgent.includes('Android')) os = 'Android'
  else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS'
  
  // Detect Browser and version
  if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) {
    browser = 'Chrome'
    const match = userAgent.match(/Chrome\/([^\s]+)/)?.[1]
    if (match) version = match.split('.')[0]
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari'
    const match = userAgent.match(/Version\/([^\s]+)/)?.[1]
    if (match) version = match.split('.')[0]
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox'
    const match = userAgent.match(/Firefox\/([^\s]+)/)?.[1]
    if (match) version = match.split('.')[0]
  } else if (userAgent.includes('Edge')) {
    browser = 'Edge'
    const match = userAgent.match(/Edg[e|A]\/([^\s]+)/)?.[1]
    if (match) version = match.split('.')[0]
  }
  
  return { browser, version, os }
}

function getBrowserDeviceType(): string {
  const userAgent = navigator.userAgent
  if (userAgent.includes('iPad')) return 'iPad'
  if (userAgent.includes('iPhone')) return 'iPhone'
  if (userAgent.includes('Android')) return 'Android'
  if (userAgent.includes('Mobile') || userAgent.includes('mobile')) return 'Mobile'
  return 'Desktop'
}
</script>

<template>
  <div class="support-widget">
    <!-- Floating Button -->
    <button 
      class="support-widget__button" 
      @click="showModal = true"
      title="Kontakt support"
      aria-label="Åbn support widget"
    >
      <TheIcon name="question" />
      <span class="support-widget__button-text">Giv feedback</span>
    </button>

    <!-- Modal Backdrop -->
    <div 
      v-if="showModal" 
      class="support-widget__backdrop" 
      @click="closeModal"
    />

    <!-- Modal -->
    <div v-if="showModal" class="support-widget__modal">
      <div class="support-widget__modal-header">
        <h2>Hjælp os med at blive bedre</h2>
        <button 
          class="support-widget__close-btn" 
          @click="closeModal"
          aria-label="Luk support widget"
        >
          ✕
        </button>
      </div>

      <!-- Info Banner -->
      <div v-if="!submissionSuccess" class="support-widget__info-banner">
        <TheIcon name="info" color="var(--support-primary)" class="support-widget__info-icon" />
        <div class="support-widget__info-content">
          <p>Oplever du fejl, eller har du feedback til siden, hører vi meget gerne fra dig her. <strong>Det hjælper os virkelig med at gøre oplevelsen bedre.</strong></p>
          <p>Vi læser alle henvendelser og svarer, så snart vi kan.</p>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="submissionSuccess" class="support-widget__success-message">
        <div class="support-widget__success-content">
          <TheIcon name="checkmark" color="var(--support-primary)" class="support-widget__success-icon" />
          <h3>Tusinde tak for din henvendelse!</h3>
          <p>Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
        </div>
      </div>

      <form v-if="!submissionSuccess" @submit.prevent="submitForm" class="support-widget__form">
        <!-- Name Field -->
        <div class="support-widget__form-group">
          <label for="support-name" class="support-widget__label">
            Navn <span class="support-widget__required">*</span>
          </label>
          <input 
            id="support-name"
            v-model="form.name"
            type="text"
            class="support-widget__input"
            :class="{ 'support-widget__input--error': errors.name }"
            placeholder="Dit navn"
            @blur="errors.name = form.name.trim() ? '' : 'Navn er påkrævet'"
          />
          <span v-if="errors.name" class="support-widget__error">{{ errors.name }}</span>
        </div>

        <!-- Email Field -->
        <div class="support-widget__form-group">
          <label for="support-email" class="support-widget__label">
            Email <span class="support-widget__required">*</span>
          </label>
          <input 
            id="support-email"
            v-model="form.email"
            type="email"
            class="support-widget__input"
            :class="{ 'support-widget__input--error': errors.email }"
            placeholder="din@email.dk"
          />
          <span v-if="errors.email" class="support-widget__error">{{ errors.email }}</span>
        </div>

        <!-- Phone Field -->
        <div class="support-widget__form-group">
          <label for="support-phone" class="support-widget__label">
            Telefon <span class="support-widget__optional">(valgfrit)</span>
          </label>
          <input 
            id="support-phone"
            v-model="form.phone"
            type="tel"
            class="support-widget__input"
            placeholder="+45 12 34 56 78"
          />
        </div>

        <!-- Message Field -->
        <div class="support-widget__form-group">
          <label for="support-message" class="support-widget__label">
            Besked <span class="support-widget__required">*</span>
          </label>
          <textarea 
            id="support-message"
            v-model="form.message"
            class="support-widget__textarea"
            :class="{ 'support-widget__textarea--error': errors.message }"
            placeholder="Beskriv dit problem eller spørgsmål her..."
            rows="5"
          />
          <span v-if="errors.message" class="support-widget__error">{{ errors.message }}</span>
        </div>

        <!-- Submit Button -->
        <button 
          type="submit" 
          class="support-widget__submit-btn"
          :disabled="loading"
        >
          <span v-if="!loading">Send Besked</span>
          <span v-else>Sender...</span>
        </button>
      </form>
    </div>
  </div>
</template>

<style scoped lang="scss">
.support-widget {
  --support-primary: #0a3c23;
  --support-button-bg: #96cd7c;
  --support-secondary: #f8f4eb;
  --support-border: #ddd;
  --support-error: #d32f2f;
  --support-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .events-naturensuge-dk & {
    --support-primary: #{$dn-naturensuge-jord};
    --support-button-bg: #{$dn-naturensuge-let};
  }

  &__button {
    position: fixed;
    bottom: 24px;
    right: 24px;
    padding: 12px 16px;
    border-radius: 28px;
    background-color: var(--support-button-bg);
    color: var(--support-primary);
    border: none;
    cursor: pointer;
    box-shadow: var(--support-shadow);
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
    z-index: 999;
    white-space: nowrap;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  &__button-text {
    font-size: 14px;
    font-weight: 500;
  }

  &__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
  }

  &__modal {
    position: fixed;
    bottom: 100px;
    right: 24px;
    width: 90%;
    max-width: 400px;
    background-color: white;
    border-radius: 8px;
    box-shadow: var(--support-shadow);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    overflow-y: auto;
  }

  &__modal-header {
    padding: 16px;
    border-bottom: 1px solid var(--support-border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      font-size: 18px;
      margin: 0;
      color: var(--support-primary);
    }
  }

  &__info-banner {
    padding: 12px 16px;
    background-color: var(--support-secondary);
    border-bottom: 1px solid var(--support-border);
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  &__info-icon {
    flex-shrink: 0;
    font-size: 20px;
    margin-top: 2px;
  }

  &__info-content {
    flex: 1;

    p {
      margin: 0;
      font-size: 13px;
      line-height: 1.4;
      color: #333;

      strong {
        color: var(--support-primary);
        font-weight: 600;
      }
    }
  }

  &__success-message {
    padding: 24px 16px;
    background-color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__success-content {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  &__success-icon {
    font-size: 48px;
    margin-bottom: 8px;
  }

  &__success-message h3 {
    margin: 0;
    font-size: 18px;
    color: var(--support-primary);
    font-weight: 600;
  }

  &__success-message p {
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
  }

  &__close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s;

    &:hover {
      color: var(--support-primary);
    }
  }

  &__form {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
  }

  &__form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }

  &__required {
    color: var(--support-error);
  }

  &__optional {
    font-size: 12px;
    color: #999;
    font-weight: normal;
  }

  &__input,
  &__textarea {
    padding: 8px 12px;
    border: 1px solid var(--support-border);
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: var(--support-primary);
      box-shadow: 0 0 0 2px rgba(10, 60, 35, 0.1);
    }

    &--error {
      border-color: var(--support-error);

      &:focus {
        box-shadow: 0 0 0 2px rgba(211, 47, 47, 0.1);
      }
    }
  }

  &__textarea {
    resize: vertical;
  }

  &__error {
    font-size: 12px;
    color: var(--support-error);
  }

  &__submit-btn {
    padding: 10px 16px;
    background-color: var(--support-primary);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background-color: #062314;
      box-shadow: var(--support-shadow);
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  // Mobile responsive
  @media (max-width: 600px) {
    &__modal {
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      max-width: none;
      border-radius: 12px 12px 0 0;
      max-height: 70vh;
    }

    &__button {
      bottom: 16px;
      right: 16px;
    }
  }
}
</style>
